import { useCallback, useEffect, useState } from "react";
import { FileStack, ChevronRight } from "lucide-react";

import MouRoundsDialog from "./MouRoundsDialog.jsx";
import { listMouCases } from "./mouCaseApi.js";
import { MOU_CASE_STATUS_OPEN, describeMouError } from "./mouCaseShared.js";
import "./mouCase.css";

/**
 * P164-R1: the MOU import process inside a job-side company's drawer, reduced
 * to what a ~440px column can honestly hold - a two-number summary and the way
 * in.
 *
 * It used to be the whole feature: the full list of rounds AND the create form,
 * inline. The owner's verdict was "ทำแบบนี้ใช้ยากมากครับ", and the measurement
 * agrees - the create form alone was five stacked fields in a 440px column, so
 * a company running three rounds could not see them and fill one in at once.
 *
 * The work moved to MouRoundsDialog, which has the whole viewport for it. What
 * stays here is the one thing a drawer is good at: telling you there is
 * something to look at, and how much of it.
 */
export default function MouCasesSection({ plcOrganizationId, plcName }) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    if (!plcOrganizationId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    listMouCases(plcOrganizationId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [plcOrganizationId]);

  useEffect(() => load(), [load]);

  const list = state.data;
  const totalCount = list?.totalCount ?? 0;
  const openCount = (list?.items ?? []).filter((round) => round.status === MOU_CASE_STATUS_OPEN).length;
  const canWrite = list?.canWrite ?? false;

  return (
    <>
      <section className="wx-wl-dsec wx-mou-section">
        <h3>การยื่น MOU</h3>

        {state.status === "error" && (
          <p className="wx-wl-inline-error" role="alert">
            {describeMouError(state.error, "ไม่สามารถอ่านรายการรอบการยื่น MOU ได้ในขณะนี้")}
          </p>
        )}

        {state.status === "loading" && state.data === null && <p className="wx-empty-list">กำลังโหลด...</p>}

        {list && (
          <button type="button" className="wx-mou-entry" onClick={() => setDialogOpen(true)}>
            <span className="wx-mou-entry-mark" aria-hidden="true">
              <FileStack size={17} />
            </span>
            <span className="wx-mou-entry-text">
              <strong>
                {totalCount === 0 ? "ยังไม่มีรอบการยื่น MOU" : `${totalCount} รอบ`}
              </strong>
              <span>
                {totalCount === 0
                  ? canWrite
                    ? "เปิดรอบแรกเพื่อเริ่มขั้นตอนการนำเข้าแรงงาน"
                    : "บริษัทฝั่งจัดหาแรงงานไม่มีขั้นตอนการยื่นนำเข้า"
                  : `กำลังดำเนินการ ${openCount} รอบ · แต่ละรอบมี 8 ขั้นตอนและเอกสารแนบ`}
              </span>
            </span>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
      </section>

      {/* Deliberately a SIBLING of the section, not a child. The dialog is
          position:fixed, so its place in the DOM buys it nothing visually - but
          inside .wx-wl-dsec it inherits the drawer's own section styling (that
          rule restyles every descendant h3 to an 11.5px uppercase label), which
          silently rewrote the step headings inside the dialog. */}
      <MouRoundsDialog
        open={dialogOpen}
        plcOrganizationId={plcOrganizationId}
        plcName={plcName}
        onClose={() => {
          setDialogOpen(false);
          load();
        }}
        onChanged={load}
      />
    </>
  );
}
